import { Injectable } from "@angular/core";
import jsPDF from "jspdf";
import { Receipt } from "../models/receipt";
import { TtcurrencyPipe } from "../pipes/ttcurrency.pipe";

const MARGIN = 12;
const LINE_H = 5.5;
const PAGE_W = 80;
const CONTENT_W = PAGE_W - MARGIN * 2;

@Injectable({ providedIn: "root" })
export class ReceiptPdfService {
  private currency = new TtcurrencyPipe();

  download(receipt: Receipt) {
    // Pass 1: measure total height using a throw-away doc
    const probe = this.makeDoc(1000);
    const totalH = this.layout(probe, receipt, false) + MARGIN;

    // Pass 2: render into a doc sized exactly to the content
    const doc = this.makeDoc(totalH);
    this.layout(doc, receipt, true);

    const filename = `bon: ${receipt.table.name}, ${receipt.date.replace(/\//g, "-")} ${receipt.time}.pdf`;
    doc.save(filename);
  }

  private makeDoc(height: number): jsPDF {
    const doc = new jsPDF({ unit: "mm", format: [PAGE_W, height] });
    doc.setFont("courier", "normal");
    return doc;
  }

  private layout(doc: jsPDF, receipt: Receipt, draw: boolean): number {
    let y = MARGIN;

    const advance = (lines: number) => { y += lines * LINE_H; };

    const center = (str: string, size: number, color: [number, number, number] = [0, 0, 0]) => {
      doc.setFontSize(size);
      if (draw) {
        doc.setTextColor(...color);
        const lines = doc.splitTextToSize(str, CONTENT_W) as string[];
        doc.text(lines, PAGE_W / 2, y, { align: "center" });
        advance(lines.length);
      } else {
        const lines = doc.splitTextToSize(str, CONTENT_W) as string[];
        advance(lines.length);
      }
    };

    const row = (left: string, right: string | null, size: number, indent = 0, color: [number, number, number] = [0, 0, 0]) => {
      doc.setFontSize(size);
      if (draw) doc.setTextColor(...color);
      const rightW = right ? doc.getTextWidth(right) + 1 : 0;
      const leftW = CONTENT_W - indent - rightW;
      const lines = doc.splitTextToSize(left, leftW) as string[];
      if (draw) {
        doc.text(lines, MARGIN + indent, y);
        if (right) doc.text(right, PAGE_W - MARGIN, y, { align: "right" });
      }
      advance(lines.length);
    };

    const divider = () => {
      y += 2;
      if (draw) {
        doc.setDrawColor(180, 180, 180);
        doc.setLineDashPattern([1, 1], 0);
        doc.line(MARGIN, y, PAGE_W - MARGIN, y);
      }
      y += 4;
    };

    // Header
    center(receipt.table.name, 13);
    center(`${receipt.date}  ${receipt.time}`, 8, [80, 80, 80]);
    if (receipt.customer.name) center(receipt.customer.name, 8, [80, 80, 80]);
    if (receipt.customer.email) center(receipt.customer.email, 8, [80, 80, 80]);

    divider();

    // Lines
    for (const line of receipt.lines) {
      if (draw) doc.setFont("courier", "normal");
      row(`${line.quantity}x  ${line.description}`, line.total > 0 ? this.currency.transform(line.total) : null, 8, 0, [0, 0, 0]);
      for (const opt of line.options) {
        row(opt.description, opt.total > 0 ? this.currency.transform(opt.total) : null, 7, 4, [110, 110, 110]);
      }
    }

    divider();

    // Total
    if (draw) {
      doc.setFont("courier", "bold");
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text("Totaal", MARGIN, y);
      doc.text(this.currency.transform(receipt.total), PAGE_W - MARGIN, y, { align: "right" });
    } else {
      doc.setFontSize(10);
    }
    advance(1);

    y += 2;
    center(receipt.payment_method, 8, [80, 80, 80]);

    return y;
  }
}
